"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Link from "next/link";
import { Fragment, useMemo, useState } from "react";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Button, Icon, InteractionLayer, Typography } from "@bookips/sds";
const MAX_ITEMS_WITHOUT_OVERFLOW = 4;
// 아이템을 행 단위로 그룹화
const groupIntoRows = (items, columns)=>{
    const rows = [];
    for(let i = 0; i < items.length; i += columns){
        rows.push(items.slice(i, i + columns));
    }
    return rows;
};
// 공통 그리드 컨테이너 스타일
const gridContainerClass = "overflow-hidden rounded-md border border-gray-200 bg-white";
// MX-359: 메뉴 아이템 클릭 이벤트 트래킹
const trackNavigationItemClick = (item, href)=>{
    mixpanelEvent("Navigation Item Clicked", {
        "Button URL": href || "",
        "Button Name": item.title,
        ID: item.categoryId,
        Position: "Edition Category Menu Grid"
    });
};
const MenuItem = (param)=>{
    let { item , isTitle , selected , href , onClick  } = param;
    const content = /*#__PURE__*/ _jsx(Typography, {
        variant: isTitle ? "subtitle1" : "body2",
        className: cn("break-words whitespace-normal", selected ? "text-primary-main" : "text-primary"),
        children: item.title
    });
    const className = "flex w-full items-center px-4 py-2";
    // MX-359: 클릭 핸들러 (이벤트 트래킹 포함)
    const handleClick = ()=>{
        trackNavigationItemClick(item, href);
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    if (href) {
        return /*#__PURE__*/ _jsx(InteractionLayer, {
            as: Link,
            href: href,
            variant: "light",
            className: className,
            onClick: handleClick,
            children: content
        });
    }
    if (onClick) {
        return /*#__PURE__*/ _jsx(InteractionLayer, {
            variant: "light",
            onClick: handleClick,
            className: className,
            children: content
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: className,
        children: content
    });
};
const CategoryColumn = (param)=>{
    let { item , selectedCategoryId , onCategorySelect , getCategoryHref , expanded , isLastRow  } = param;
    var _item_items;
    /*#__PURE__*/ return _jsxs("div", {
        className: cn("flex min-w-0 flex-1 flex-col border-r border-gray-100 py-2.5 sm:min-w-[180px]", !isLastRow && "border-b", expanded ? "min-h-[220px]" : "h-[220px]"),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "shrink-0",
                children: /*#__PURE__*/ _jsx(MenuItem, {
                    item: item,
                    isTitle: true,
                    href: getCategoryHref === null || getCategoryHref === void 0 ? void 0 : getCategoryHref(item),
                    onClick: ()=>{
                        return onCategorySelect === null || onCategorySelect === void 0 ? void 0 : onCategorySelect(item.categoryId, item.productId);
                    }
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: cn("flex-1", expanded ? "overflow-visible" : "overflow-y-auto custom-scrollbar"),
                children: (_item_items = item.items) === null || _item_items === void 0 ? void 0 : _item_items.map((child)=>{
                    /*#__PURE__*/ return _jsx(MenuItem, {
                        item: child,
                        selected: selectedCategoryId === child.categoryId,
                        href: getCategoryHref === null || getCategoryHref === void 0 ? void 0 : getCategoryHref(child),
                        onClick: ()=>{
                            return onCategorySelect === null || onCategorySelect === void 0 ? void 0 : onCategorySelect(child.categoryId, child.productId);
                        }
                    }, child.categoryId);
                })
            })
        ]
    });
};
const TitleCell = (param)=>{
    let { item , href , onClick , columns , isLastRow  } = param;
    const cellClass = cn("flex items-center justify-center border-gray-100 px-1 py-3 border-r", columns === 3 ? "[&:nth-child(3n)]:border-r-0" : "[&:nth-child(4n)]:border-r-0", !isLastRow && "border-b");
    const content = /*#__PURE__*/ _jsx(Typography, {
        variant: "body2",
        className: "text-primary text-center break-keep",
        children: item.title
    });
    // MX-359: 클릭 핸들러 (이벤트 트래킹 포함)
    const handleClick = ()=>{
        trackNavigationItemClick(item, href);
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    if (href) {
        return /*#__PURE__*/ _jsx(InteractionLayer, {
            as: Link,
            href: href,
            variant: "light",
            className: cellClass,
            onClick: handleClick,
            children: content
        });
    }
    return /*#__PURE__*/ _jsx(InteractionLayer, {
        variant: "light",
        onClick: handleClick,
        className: cellClass,
        children: content
    });
};
const TitleGrid = (param)=>{
    let { items , columns , className , onCategorySelect , getCategoryHref  } = param;
    const rows = groupIntoRows(items, columns);
    const gridColsClass = columns === 3 ? "grid-cols-3" : "grid-cols-4";
    return /*#__PURE__*/ _jsx("div", {
        className: cn(gridContainerClass, "grid", gridColsClass, className),
        children: rows.map((row, rowIndex)=>{
            const isLastRow = rowIndex === rows.length - 1;
            const emptyCount = columns - row.length;
            return /*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    row.map((item)=>{
                        /*#__PURE__*/ return _jsx(TitleCell, {
                            item: item,
                            href: getCategoryHref === null || getCategoryHref === void 0 ? void 0 : getCategoryHref(item),
                            onClick: ()=>{
                                return onCategorySelect === null || onCategorySelect === void 0 ? void 0 : onCategorySelect(item.categoryId, item.productId);
                            },
                            columns: columns,
                            isLastRow: isLastRow
                        }, item.categoryId);
                    }),
                    Array.from({
                        length: emptyCount
                    }).map((_, i)=>/*#__PURE__*/ _jsx("div", {
                            className: cn("border-r border-gray-100", columns === 3 ? "[&:nth-child(3n)]:border-r-0" : "[&:nth-child(4n)]:border-r-0", !isLastRow && "border-b")
                        }, "empty-".concat(i)))
                ]
            }, rowIndex);
        })
    });
};
const ColumnGrid = (param)=>{
    let { items , columns , className , selectedCategoryId , onCategorySelect , getCategoryHref , expanded  } = param;
    const rows = groupIntoRows(items, columns);
    return /*#__PURE__*/ _jsx("div", {
        className: cn(gridContainerClass, className),
        children: rows.map((row, rowIndex)=>{
            const isLastRow = rowIndex === rows.length - 1;
            return /*#__PURE__*/ _jsxs("div", {
                className: "flex [&>*:last-child]:border-r-0",
                children: [
                    row.map((item)=>/*#__PURE__*/ _jsx(CategoryColumn, {
                            item: item,
                            selectedCategoryId: selectedCategoryId,
                            onCategorySelect: onCategorySelect,
                            getCategoryHref: getCategoryHref,
                            expanded: expanded,
                            isLastRow: isLastRow
                        }, item.categoryId)),
                    row.length < columns && Array.from({
                        length: columns - row.length
                    }).map((_, i)=>/*#__PURE__*/ _jsx("div", {
                            className: cn("min-w-0 flex-1 border-r border-gray-100 sm:min-w-[180px]", !isLastRow && "border-b", expanded ? "min-h-[220px]" : "h-[220px]")
                        }, "empty-".concat(i)))
                ]
            }, rowIndex);
        })
    });
};
/**
 * 카테고리 메뉴 그리드 컴포넌트
 */ export const CategoryMenuGrid = (param)=>{
    let { className , title , items , selectedCategoryId , onCategorySelect , defaultExpanded =false , expandable =true , getCategoryHref , guideBanner , variant ="full"  } = param;
    const [expanded, setExpanded] = useState(defaultExpanded);
    const hasOverflow = useMemo(()=>{
        return expandable && items.some((item)=>{
            var _item_items;
            return (((_item_items = item.items) === null || _item_items === void 0 ? void 0 : _item_items.length) || 0) > MAX_ITEMS_WITHOUT_OVERFLOW;
        });
    }, [
        items,
        expandable
    ]);
    const commonGridProps = {
        items,
        onCategorySelect,
        getCategoryHref
    };
    // titleOnly 모드
    if (variant === "titleOnly") {
        return /*#__PURE__*/ _jsxs("section", {
            className: cn("flex flex-col gap-2", className),
            children: [
                title && /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    className: "text-strong",
                    children: "더 자세히 찾기"
                }),
                /*#__PURE__*/ _jsx(TitleGrid, {
                    ...commonGridProps,
                    columns: 3,
                    className: "md:hidden"
                }),
                /*#__PURE__*/ _jsx(TitleGrid, {
                    ...commonGridProps,
                    columns: 4,
                    className: "hidden md:grid"
                })
            ]
        });
    }
    // full 모드
    return /*#__PURE__*/ _jsxs("section", {
        className: cn("flex flex-col gap-2", className),
        children: [
            title && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h6",
                        className: "text-strong hidden sm:block",
                        children: "전체 리스트"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "subtitle1",
                        className: "text-strong sm:hidden",
                        children: "더 자세히 찾기"
                    })
                ]
            }),
            guideBanner && /*#__PURE__*/ _jsx("div", {
                className: "hidden sm:block",
                children: guideBanner
            }),
            /*#__PURE__*/ _jsx(TitleGrid, {
                ...commonGridProps,
                columns: 3,
                className: "sm:hidden"
            }),
            /*#__PURE__*/ _jsx(ColumnGrid, {
                ...commonGridProps,
                columns: 3,
                selectedCategoryId: selectedCategoryId,
                expanded: expanded,
                className: "hidden sm:block md:hidden"
            }),
            /*#__PURE__*/ _jsx(ColumnGrid, {
                ...commonGridProps,
                columns: 4,
                selectedCategoryId: selectedCategoryId,
                expanded: expanded,
                className: "hidden md:block"
            }),
            expandable && hasOverflow && /*#__PURE__*/ _jsx(Button, {
                variant: "stroke",
                color: "secondary",
                size: "md",
                fullWidth: true,
                onClick: ()=>{
                    // MX-358: 펼치기/접기 버튼 클릭 이벤트
                    mixpanelEvent("Expand Button Clicked", {
                        "Button Name": expanded ? "접기" : "펼치기",
                        Position: "Edition Category Menu Grid"
                    });
                    setExpanded(!expanded);
                },
                className: "hidden sm:flex",
                children: /*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center gap-1",
                    children: [
                        expanded ? "접기" : "펼치기",
                        /*#__PURE__*/ _jsx(Icon.Triangle, {
                            className: cn("size-4.5 text-gray-700 transition-transform", expanded && "rotate-180")
                        })
                    ]
                })
            })
        ]
    });
};
